import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ScienceRounded,
} from "@mui/icons-material";
import { ExtractorName } from "../../api/documentIntelligence";
import {
  getLlmLabDocuments,
  getLlmLabRun,
  listLlmLabCompanyDocuments,
  LlmLabDocument,
  LlmLabDocumentsResponse,
  LlmLabResult,
  LlmLabRun,
  searchLlmLabCompanies,
  startLlmLabRun,
  stopLlmLabRun,
} from "../../api/llmLab";
import { LlmModelOption, LlmModelSource } from "../../api/llmJobs";
import { CompanyListItem } from "../../types/company";

const ACTIVE_RUN_KEY = "babbage.llmLab.activeRun";
const BLOCKED_LAB_MODELS = new Set([
  "google/gemini-3.5-flash-lite",
  "google/gemini-3.6-flash",
]);
const TERMINAL_STATUSES = ["completed", "degraded", "failed", "cancelled"];
const ACTIVE_STATUSES = [
  "queued",
  "queued_v2",
  "queued_v3",
  "running",
  "running_v2",
  "running_v3",
  "cancelling",
  "cancelling_v2",
  "cancelling_v3",
];
const ANALYSIS_LABELS: Record<ExtractorName, string> = {
  milestones: "Milestones",
  pipeline_intelligence: "Pipeline",
  corporate_transactions: "Corporate Transactions",
};

const money = (value?: number | null): string =>
  value == null ? "—" : `$${value.toFixed(value >= 1 ? 2 : 4)}`;

const documentKey = (document: LlmLabDocument): string =>
  `${document.company_id}:${document.source_type}:${document.content_id}`;

interface Props {
  openRouterSource?: LlmModelSource;
}

const LlmLabPanel: React.FC<Props> = ({ openRouterSource }) => {
  const [corpus, setCorpus] = useState<LlmLabDocumentsResponse | null>(null);
  const [selectedModels, setSelectedModels] = useState<LlmModelOption[]>([]);
  const [analyses, setAnalyses] = useState<ExtractorName[]>([
    "corporate_transactions",
  ]);
  const [companyOptions, setCompanyOptions] = useState<CompanyListItem[]>([]);
  const [companyQuery, setCompanyQuery] = useState("");
  const [selectedCompany, setSelectedCompany] =
    useState<CompanyListItem | null>(null);
  const [searchingCompanies, setSearchingCompanies] = useState(false);
  const [daysBack, setDaysBack] = useState(365);
  const [availableDocuments, setAvailableDocuments] = useState<
    LlmLabDocument[]
  >([]);
  const [selectedDocumentKeys, setSelectedDocumentKeys] = useState<Set<string>>(
    new Set(),
  );
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const documentRequestGeneration = useRef(0);
  const [run, setRun] = useState<LlmLabRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const models = useMemo(
    () =>
      (openRouterSource?.models || []).filter(
        (item) => !BLOCKED_LAB_MODELS.has(item.id),
      ),
    [openRouterSource?.models],
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const docs = await getLlmLabDocuments();
        if (cancelled) return;
        setCorpus(docs);
        setAvailableDocuments(docs.documents);
        setSelectedDocumentKeys(new Set(docs.documents.map(documentKey)));
        const preferred = [
          "google/gemini-3.5-flash",
          "qwen/qwen3-235b-a22b-2507",
        ];
        const initial = preferred
          .map((id) => models.find((item) => item.id === id))
          .filter((item): item is LlmModelOption => Boolean(item));
        setSelectedModels(initial.length ? initial : models.slice(0, 3));
        const activeRunId = window.localStorage.getItem(ACTIVE_RUN_KEY);
        if (activeRunId) {
          try {
            setRun(await getLlmLabRun(activeRunId));
          } catch {
            window.localStorage.removeItem(ACTIVE_RUN_KEY);
          }
        }
      } catch {
        if (!cancelled) setError("Could not load the LLM Lab corpus.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [models]);

  useEffect(() => {
    if (companyQuery.trim().length < 2) {
      setCompanyOptions([]);
      return;
    }
    let cancelled = false;
    const handle = window.setTimeout(async () => {
      setSearchingCompanies(true);
      try {
        const response = await searchLlmLabCompanies(companyQuery.trim());
        if (!cancelled) setCompanyOptions(response.results || []);
      } catch {
        if (!cancelled) setCompanyOptions([]);
      } finally {
        if (!cancelled) setSearchingCompanies(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [companyQuery]);

  const activeRunId = run?.run_id;
  const activeRunStatus = run?.status;

  useEffect(() => {
    if (
      !activeRunId ||
      !activeRunStatus ||
      !ACTIVE_STATUSES.includes(activeRunStatus)
    )
      return;
    let cancelled = false;
    const handle = window.setInterval(async () => {
      try {
        const next = await getLlmLabRun(activeRunId);
        if (cancelled) return;
        setRun(next);
        if (TERMINAL_STATUSES.includes(next.status)) {
          window.clearInterval(handle);
          window.localStorage.removeItem(ACTIVE_RUN_KEY);
        }
      } catch {
        if (!cancelled) setError("Could not refresh the active Lab run.");
      }
    }, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [activeRunId, activeRunStatus]);

  const running = Boolean(run && ACTIVE_STATUSES.includes(run.status));
  const [stopping, setStopping] = useState(false);
  const progress = run?.documents_total
    ? ((run.documents_completed || 0) / run.documents_total) * 100
    : 0;

  const toggleAnalysis = (analysis: ExtractorName) => {
    setAnalyses((current) =>
      current.includes(analysis)
        ? current.filter((item) => item !== analysis)
        : [...current, analysis],
    );
  };

  const selectedDocuments = useMemo(
    () =>
      availableDocuments.filter((document) =>
        selectedDocumentKeys.has(documentKey(document)),
      ),
    [availableDocuments, selectedDocumentKeys],
  );

  const loadCompanyDocuments = async () => {
    if (!selectedCompany) return;
    const company = selectedCompany;
    const requestGeneration = ++documentRequestGeneration.current;
    setLoadingDocuments(true);
    setError(null);
    try {
      const response = await listLlmLabCompanyDocuments(company.id, daysBack);
      if (requestGeneration !== documentRequestGeneration.current) return;
      const documents = (response.documents || []).map((document) => ({
        ...document,
        company_name:
          document.company_name || response.company_name || company.name,
        category: document.category || "selected",
      }));
      setAvailableDocuments(documents);
      setSelectedDocumentKeys(new Set());
      if (!documents.length) {
        setError(
          `No Production documents found for ${company.name} in this lookback.`,
        );
      }
    } catch (err) {
      if (requestGeneration !== documentRequestGeneration.current) return;
      const detail = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      setError(detail || "Could not load company documents.");
    } finally {
      if (requestGeneration === documentRequestGeneration.current) {
        setLoadingDocuments(false);
      }
    }
  };

  const toggleDocument = (document: LlmLabDocument) => {
    const key = documentKey(document);
    setSelectedDocumentKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else if (next.size < (corpus?.max_documents || 25)) next.add(key);
      return next;
    });
  };

  const start = async () => {
    if (!selectedModels.length || !analyses.length || !selectedDocuments.length)
      return;
    setStarting(true);
    setError(null);
    try {
      const next = await startLlmLabRun(
        selectedModels.map((item) => item.id),
        analyses,
        selectedDocuments,
      );
      setRun(next);
      window.localStorage.setItem(ACTIVE_RUN_KEY, next.run_id);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      setError(detail || "Could not start the LLM Lab run.");
    } finally {
      setStarting(false);
    }
  };

  const stop = async () => {
    if (!run?.run_id) return;
    setStopping(true);
    setError(null);
    try {
      setRun(await stopLlmLabRun(run.run_id));
    } catch {
      setError("Could not stop the LLM Lab run.");
    } finally {
      setStopping(false);
    }
  };

  const resultRows = useMemo(
    () => (run?.results || []).filter((item) => item.model),
    [run?.results],
  );

  const documentSummaries = useMemo(() => {
    const grouped = new Map<
      string,
      {
        title: string;
        category: string;
        models: Map<string, Record<string, LlmLabResult>>;
      }
    >();
    resultRows.forEach((row) => {
      const current = grouped.get(row.content_id) || {
        title: row.document_title,
        category: row.category,
        models: new Map(),
      };
      const byModel = current.models.get(row.model) || {};
      byModel[row.extractor] = row;
      current.models.set(row.model, byModel);
      grouped.set(row.content_id, current);
    });
    return Array.from(grouped.entries()).map(([contentId, value]) => ({
      contentId,
      ...value,
    }));
  }, [resultRows]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <Alert severity="success">
        <strong>Protected benchmark:</strong> every model call is pinned to an
        explicitly tagged North American provider endpoint, with Zero Data
        Retention and data collection denial. Runs are preview-only and do not
        write the production spend ledger or extraction stores.
      </Alert>
      {error && <Alert severity="error">{error}</Alert>}

      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Document Intelligence benchmark
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose any Production company and up to{" "}
              {corpus?.max_documents || 25} specific documents · compare up to{" "}
              {corpus?.max_models || 6} models without canonical writes.
            </Typography>
          </Box>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ md: "flex-start" }}
          >
            <Autocomplete
              sx={{ flex: 1, minWidth: 260 }}
              options={companyOptions}
              value={selectedCompany}
              inputValue={companyQuery}
              loading={searchingCompanies}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              onInputChange={(_event, value) => setCompanyQuery(value)}
              onChange={(_event, value) => {
                documentRequestGeneration.current += 1;
                setLoadingDocuments(false);
                setSelectedCompany(value);
                setAvailableDocuments([]);
                setSelectedDocumentKeys(new Set());
                if (value) setCompanyQuery(value.name);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Production company"
                  helperText="Type a company name or ticker, then choose a result."
                />
              )}
            />
            <TextField
              label="Document lookback (days)"
              type="number"
              value={daysBack}
              onChange={(event) => {
                documentRequestGeneration.current += 1;
                setLoadingDocuments(false);
                setDaysBack(Math.max(1, Number(event.target.value) || 1));
                setAvailableDocuments([]);
                setSelectedDocumentKeys(new Set());
              }}
              inputProps={{ min: 1 }}
              sx={{ width: { xs: "100%", md: 190 } }}
            />
            <Button
              variant="outlined"
              disabled={!selectedCompany || loadingDocuments || running}
              onClick={() => void loadCompanyDocuments()}
              sx={{ minHeight: 56 }}
            >
              {loadingDocuments ? "Loading…" : "Load documents"}
            </Button>
          </Stack>
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
            >
              <Box>
                <Typography variant="subtitle2">
                  Documents to compare ({selectedDocuments.length} selected)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedCompany
                    ? `Production documents for ${selectedCompany.name}`
                    : "Default Production benchmark corpus"}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  disabled={!availableDocuments.length || running}
                  onClick={() =>
                    setSelectedDocumentKeys(
                      new Set(
                        availableDocuments
                          .slice(0, corpus?.max_documents || 25)
                          .map(documentKey),
                      ),
                    )
                  }
                >
                  Select all
                </Button>
                <Button
                  size="small"
                  disabled={!selectedDocumentKeys.size || running}
                  onClick={() => setSelectedDocumentKeys(new Set())}
                >
                  Clear
                </Button>
                <Button
                  size="small"
                  disabled={running}
                  onClick={() => {
                    documentRequestGeneration.current += 1;
                    setLoadingDocuments(false);
                    const defaults = corpus?.documents || [];
                    setSelectedCompany(null);
                    setCompanyQuery("");
                    setAvailableDocuments(defaults);
                    setSelectedDocumentKeys(new Set(defaults.map(documentKey)));
                  }}
                >
                  Use default corpus
                </Button>
              </Stack>
            </Stack>
            <Stack
              spacing={0.25}
              sx={{ mt: 1, maxHeight: 280, overflowY: "auto" }}
            >
              {availableDocuments.map((document) => (
                <FormControlLabel
                  key={documentKey(document)}
                  control={
                    <Checkbox
                      checked={selectedDocumentKeys.has(documentKey(document))}
                      onChange={() => toggleDocument(document)}
                      disabled={running}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">{document.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {document.source_type === "presentation"
                          ? "Presentation"
                          : "Press release"}
                        {document.published_date
                          ? ` · ${new Date(document.published_date).toLocaleDateString()}`
                          : ""}
                      </Typography>
                    </Box>
                  }
                />
              ))}
              {!availableDocuments.length && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 1 }}
                >
                  Load a company to choose its documents.
                </Typography>
              )}
            </Stack>
          </Paper>
          <Autocomplete
            multiple
            options={models}
            value={selectedModels}
            limitTags={4}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_event, value) =>
              setSelectedModels(value.slice(0, corpus?.max_models || 6))
            }
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box>
                  <Typography variant="body2">{option.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.id} · {money(option.input_cost_per_million)}/M in ·{" "}
                    {money(option.output_cost_per_million)}/M out
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Models to compare"
                helperText="Only models with explicitly tagged North American OpenRouter endpoints are shown."
              />
            )}
          />
          <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
            {(corpus?.analyses || []).map((analysis) => (
              <FormControlLabel
                key={analysis}
                control={
                  <Checkbox
                    checked={analyses.includes(analysis)}
                    onChange={() => toggleAnalysis(analysis)}
                  />
                }
                label={ANALYSIS_LABELS[analysis]}
              />
            ))}
          </Stack>
          {!corpus?.worker_enabled && (
            <Alert severity="info">
              LLM Lab execution is disabled for this deployment to protect
              production job capacity. An operator can enable the dedicated
              worker explicitly.
            </Alert>
          )}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ sm: "center" }}
          >
            <Button
              variant="contained"
              startIcon={
                starting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <ScienceRounded />
                )
              }
              disabled={
                !corpus?.worker_enabled ||
                starting ||
                running ||
                !selectedModels.length ||
                !analyses.length ||
                !selectedDocuments.length
              }
              onClick={() => void start()}
            >
              {running
                ? "Comparison running"
                : starting
                  ? "Starting…"
                  : `Run ${selectedDocuments.length}-document comparison`}
            </Button>
            {running && (
              <Button
                color="error"
                variant="outlined"
                disabled={stopping || run?.status === "cancelling"}
                onClick={() => void stop()}
              >
                {run?.status === "cancelling" || stopping
                  ? "Stopping…"
                  : "Stop benchmark"}
              </Button>
            )}
            <Typography variant="caption" color="text.secondary">
              Cost scales as selected models × selected documents × selected
              analyses and incurs real OpenRouter usage.
            </Typography>
          </Stack>
        </Stack>
      </Paper>

      {run && (
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Stack spacing={1}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6">Run status</Typography>
              <Chip
                label={run.status}
                color={
                  run.status === "completed"
                    ? "success"
                    : run.status === "degraded"
                      ? "warning"
                      : run.status === "failed"
                        ? "error"
                        : run.status === "cancelled"
                          ? "default"
                          : "primary"
                }
                size="small"
              />
            </Stack>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="body2" color="text.secondary">
              {run.documents_completed || 0} / {run.documents_total || 10}{" "}
              documents
              {run.current_document ? ` · ${run.current_document}` : ""}
            </Typography>
            {run.error && <Alert severity="error">{run.error}</Alert>}
            {run.status === "cancelled" && (
              <Alert severity="info">
                Benchmark stopped. Completed documents stay visible below.
              </Alert>
            )}
            {run.status === "degraded" && (
              <Alert severity="warning">
                Benchmark completed with failed document/model analyses. Failed
                rows count as zero quality in rankings.
              </Alert>
            )}
          </Stack>
        </Paper>
      )}

      {!!run?.summary?.length && (
        <TableContainer component={Paper} variant="outlined">
          <Box sx={{ p: 2 }}>
            <Typography variant="h6">Model ranking</Typography>
            <Typography variant="caption" color="text.secondary">
              Quality is shown only where stored reference findings exist;
              zero-reference rows are marked not scored. Value rank = scored
              quality per billed/estimated dollar.
            </Typography>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Rank</TableCell>
                <TableCell>Model</TableCell>
                <TableCell align="right">Quality</TableCell>
                <TableCell align="right">Success</TableCell>
                <TableCell align="right">Total cost</TableCell>
                <TableCell align="right">Quality / $</TableCell>
                <TableCell align="right">Avg latency</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {run.summary.map((item) => (
                <TableRow key={item.model}>
                  <TableCell>#{item.value_rank}</TableCell>
                  <TableCell>
                    <code>{item.model}</code>
                  </TableCell>
                  <TableCell align="right">
                    {item.quality_score == null
                      ? "Not scored"
                      : item.quality_score.toFixed(1)}
                  </TableCell>
                  <TableCell align="right">
                    {(item.success_rate * 100).toFixed(0)}%
                  </TableCell>
                  <TableCell align="right">
                    {money(item.total_cost_usd)}
                  </TableCell>
                  <TableCell align="right">
                    {item.quality_per_dollar?.toFixed(1) || "—"}
                  </TableCell>
                  <TableCell align="right">
                    {item.average_latency_ms.toLocaleString()} ms
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {documentSummaries.map((document) => (
        <Paper key={document.contentId} variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6">{document.title}</Typography>
          <Typography variant="caption" color="text.secondary">
            {document.category}
          </Typography>
          <TableContainer sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Model</TableCell>
                  <TableCell>Milestones</TableCell>
                  <TableCell>Pipeline</TableCell>
                  <TableCell>Corporate Transactions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from(document.models.entries()).map(
                  ([model, analyses]) => (
                    <TableRow
                      key={`${document.contentId}-${model}`}
                      sx={{ verticalAlign: "top" }}
                    >
                      <TableCell sx={{ minWidth: 180 }}>
                        <code>{model}</code>
                      </TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        <ItemSnippets result={analyses.milestones} />
                      </TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        <ItemSnippets result={analyses.pipeline_intelligence} />
                      </TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        <ItemSnippets
                          result={analyses.corporate_transactions}
                        />
                      </TableCell>
                    </TableRow>
                  ),
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ))}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2">Fixed benchmark documents</Typography>
        <Stack
          direction="row"
          spacing={0.75}
          useFlexGap
          flexWrap="wrap"
          sx={{ mt: 1 }}
        >
          {(corpus?.documents || []).map((document) => (
            <Chip
              key={document.content_id}
              size="small"
              variant="outlined"
              label={`${document.title} · ${document.source_type === "presentation" ? "Deck" : "PR"}`}
            />
          ))}
        </Stack>
      </Paper>
    </Stack>
  );
};

export default LlmLabPanel;

const ItemSnippets: React.FC<{ result?: LlmLabResult }> = ({ result }) => {
  if (!result) {
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }
  if (!result.ok) {
    return (
      <Stack spacing={0.5}>
        <Typography variant="caption" color="error">
          {result.error || "failed"}
        </Typography>
        <ResultDiagnostics result={result} />
      </Stack>
    );
  }
  const items = result.items || [];
  if (!items.length) {
    if ((result.item_count || 0) > 0) {
      return (
        <Typography variant="caption" color="text.secondary">
          {result.item_count} {result.item_count === 1 ? "item" : "items"} found
          · details unavailable for this older run
        </Typography>
      );
    }
    return (
      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">
          0 items
        </Typography>
        <ResultDiagnostics result={result} />
      </Stack>
    );
  }
  const preview = items[0];
  return (
    <Stack spacing={0.5}>
      <Accordion
        disableGutters
        elevation={0}
        sx={{ bgcolor: "transparent", "&:before": { display: "none" } }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ px: 0, minHeight: 0 }}
        >
          <Typography variant="body2">
            {preview.title}
            {items.length > 1 ? ` +${items.length - 1}` : ""}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pt: 0 }}>
          <Stack spacing={0.75}>
            {items.map((item, index) => (
              <Box key={`${item.title}-${index}`}>
                <Typography variant="body2">{item.title}</Typography>
                {item.detail && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {item.detail}
                  </Typography>
                )}
                {item.extra && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {item.extra}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
      <ResultDiagnostics result={result} />
    </Stack>
  );
};

const ResultDiagnostics: React.FC<{ result: LlmLabResult }> = ({ result }) => {
  const normalized = result.normalized_output || [];
  if (!result.raw_output && !normalized.length) return null;
  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{ bgcolor: "transparent", "&:before": { display: "none" } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ px: 0, minHeight: 0 }}
      >
        <Typography variant="caption" color="text.secondary">
          Diagnostics
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pt: 0 }}>
        <Stack spacing={1}>
          {result.raw_output && (
            <Box>
              <Typography variant="caption" fontWeight={700}>
                Raw model output
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  mt: 0.5,
                  p: 1,
                  maxHeight: 220,
                  overflow: "auto",
                  fontSize: 11,
                  whiteSpace: "pre-wrap",
                  bgcolor: "action.hover",
                }}
              >
                {result.raw_output}
              </Box>
            </Box>
          )}
          {!!normalized.length && (
            <Box>
              <Typography variant="caption" fontWeight={700}>
                Normalized output
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  mt: 0.5,
                  p: 1,
                  maxHeight: 220,
                  overflow: "auto",
                  fontSize: 11,
                  whiteSpace: "pre-wrap",
                  bgcolor: "action.hover",
                }}
              >
                {JSON.stringify(normalized, null, 2)}
              </Box>
            </Box>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
